// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import {
  BottomSheet,
  CbhIcon,
  DistanceSlider,
  Input,
  Text,
  TextButton,
  useModalState,
} from "@clipboard-health/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
// TODO: Use our own Button
// eslint-disable-next-line no-restricted-imports
import { Box, Button, Divider, Grid, Link, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import {
  areCommaSeparatedSetsEqual,
  defaultAgeGroups,
  defaultDaysOfWeek,
  defaultJobTypes,
  defaultShiftTypes,
  jobTypeOptions,
  shiftTypeOptions,
} from "@src/appV2/redesign/PlacementCandidate/constants";
import { type Worker } from "@src/appV2/Worker/api/types";
import { useWorkerQualifications } from "@src/appV2/Worker/useWorkerQualifications";
import { isEmpty } from "lodash";
import { type ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import { StickyFooter } from "../../components/StickyFooter";
import { useGetWorkerOpenPlacementFilters } from "../../PlacementCandidate/api/useGetWorkerOpenPlacementFilters";
import { useGetTags } from "../api/useGetTags";
import { type GetPlacementsForWorkplaceQuery } from "../types/fetchPlacements.schema";
import { createTagOptions, hasChildcareQualification } from "../utils";
import { getFilterGroupDisplayName, translateFilterForBackend } from "../utils/filter-translation";
import { FilterChips } from "./FilterChips";

type SectionKey =
  | "jobTypes"
  | "shiftTypes"
  | "daysOfWeek"
  | "distanceInMiles"
  | "workerTypes"
  | "ageGroups";

interface FilterButtonProps {
  filter?: GetPlacementsForWorkplaceQuery["filter"];
  setFilter: (filter: GetPlacementsForWorkplaceQuery["filter"]) => void;
  worker: Worker;
  placementCandidateId: string;
  /**
   * Optional custom trigger renderer. If provided, the default button is not rendered.
   * Use the provided `open` function to open the modal.
   */
  renderTrigger?: (triggerProps: {
    open: (sectionKey?: string | unknown) => void;
    totalAppliedFilters: number;
  }) => ReactNode;
}

const filterFormSchema = z.object({
  distanceInMiles: z.number().min(0).max(150).optional(),
  jobType: z.string({ required_error: "Select at least one job type" }).min(1, {
    message: "Select at least one job type",
  }),
  shiftTime: z.string({ required_error: "Select at least one shift time" }).min(1, {
    message: "Select at least one shift time",
  }),
  workerTypes: z.string({ required_error: "Select at least one worker type" }).min(1, {
    message: "Select at least one worker type",
  }),
  daysOfWeek: z.string({ required_error: "Select at least one day of the week" }).min(1, {
    message: "Select at least one day of the week",
  }),
  ageGroups: z.string().optional(),
});

const MIN_DISTANCE = 0;

type FilterFormData = z.infer<typeof filterFormSchema>;

function transformDaysOfWeekFromApiToForm(value: string): string {
  const weekendsIncluded = value.includes("SATURDAY") || value.includes("SUNDAY");

  const weekdaysIncluded =
    value.includes("MONDAY") ||
    value.includes("TUESDAY") ||
    value.includes("WEDNESDAY") ||
    value.includes("THURSDAY") ||
    value.includes("FRIDAY");

  let outputFilter: string[] = [];

  if (weekdaysIncluded) {
    outputFilter = ["MONDAY,TUESDAY,WEDNESDAY,THURSDAY,FRIDAY"];
  }

  if (weekendsIncluded) {
    outputFilter = [...outputFilter, "SATURDAY,SUNDAY"];
  }

  return outputFilter.join("|");
}

function countTotalAppliedFilters(
  watchedValues: Partial<FilterFormData>,
  defaultWorkerTypes: string
): number {
  if (!watchedValues) {
    return 0;
  }

  let count = 0;

  // 1. Distance: default 0 (no filter). Any value > 0 is considered applied.
  const distance = watchedValues.distanceInMiles ?? 0;
  if (distance > 0) {
    count += 1;
  }

  // 2. Job Types
  if (
    watchedValues.jobType &&
    !areCommaSeparatedSetsEqual(watchedValues.jobType, defaultJobTypes)
  ) {
    count += 1;
  }

  // 3. Shift Types
  if (
    watchedValues.shiftTime &&
    !areCommaSeparatedSetsEqual(watchedValues.shiftTime, defaultShiftTypes)
  ) {
    count += 1;
  }

  // 4. Days of Week (delimiter is comma but options themselves may contain commas)
  if (
    watchedValues.daysOfWeek &&
    !areCommaSeparatedSetsEqual(
      watchedValues.daysOfWeek.replace(/\|/g, ","),
      defaultDaysOfWeek.replace(/\|/g, ",")
    )
  ) {
    count += 1;
  }

  // 5. Worker Types
  if (
    watchedValues.workerTypes &&
    !areCommaSeparatedSetsEqual(watchedValues.workerTypes, defaultWorkerTypes)
  ) {
    count += 1;
  }

  return count;
}

export function FilterButton(props: FilterButtonProps) {
  const { filter, setFilter, worker, placementCandidateId, renderTrigger } = props;
  const filterModalState = useModalState();

  // Refs for each section so that we can scroll to them when the modal is opened
  const jobTypesRef = useRef<HTMLDivElement>(null);
  const shiftTypesRef = useRef<HTMLDivElement>(null);
  const daysOfWeekRef = useRef<HTMLDivElement>(null);
  const distanceInMilesRef = useRef<HTMLDivElement>(null);
  const workerTypesRef = useRef<HTMLDivElement>(null);
  const ageGroupsRef = useRef<HTMLDivElement>(null);

  const sectionRefMap = useMemo<Record<SectionKey, React.RefObject<HTMLDivElement>>>(
    () => ({
      jobTypes: jobTypesRef,
      shiftTypes: shiftTypesRef,
      daysOfWeek: daysOfWeekRef,
      distanceInMiles: distanceInMilesRef,
      workerTypes: workerTypesRef,
      ageGroups: ageGroupsRef,
    }),
    []
  );

  // Store which section we should scroll to when the modal finishes opening
  const scrollTargetKeyRef = useRef<SectionKey | undefined>(undefined);
  const { data: allWorkerQualifications } = useWorkerQualifications();

  const { data: workerTypesAvailableForHiring, isLoading: isLoadingWorkerTypes } =
    useGetWorkerOpenPlacementFilters({
      workerId: worker.userId,
      useHiringWorkerTypes: true,
    });

  // Filter qualifications based on available worker types from API
  // Only filter if workerTypesAvailableForHiring has values
  const workerQualifications = useMemo(() => {
    if (!workerTypesAvailableForHiring || workerTypesAvailableForHiring.length === 0) {
      return allWorkerQualifications;
    }

    const filtered = allWorkerQualifications?.filter((qualification) =>
      workerTypesAvailableForHiring.includes(qualification.attributes.name)
    );

    // If filtering results in empty array, use all items from workerTypesAvailableForHiring
    if (!filtered || filtered.length === 0) {
      return [];
    }

    return filtered;
  }, [allWorkerQualifications, workerTypesAvailableForHiring]);

  // Create worker type options that include both matched and unmatched types
  const workerTypeOptions = useMemo(() => {
    if (!workerTypesAvailableForHiring || workerTypesAvailableForHiring.length === 0) {
      return (
        workerQualifications?.map((qualification) => ({
          label: qualification.attributes.displayLabel,
          value: qualification.attributes.name,
        })) ?? []
      );
    }

    return workerTypesAvailableForHiring.map((workerType) => {
      const matchingQualification = allWorkerQualifications?.find(
        (qual) => qual.attributes.name === workerType
      );

      return {
        label: matchingQualification?.attributes.displayLabel ?? workerType,
        value: workerType,
      };
    });
  }, [workerTypesAvailableForHiring, allWorkerQualifications, workerQualifications]);

  // Check if worker has childcare qualifications to enable age group tags
  const qualificationNames =
    workerQualifications?.map((qualification) => qualification.attributes.name) ?? [];
  const isChildcareWorker = hasChildcareQualification(qualificationNames);

  const { data: tagsData } = useGetTags(
    {
      entityType: "placement",
    },
    {
      enabled: isChildcareWorker,
    }
  );

  // Memoize the string of all worker qualifications for default and reset states
  // Use workerTypesAvailableForHiring when available, otherwise fall back to workerQualifications
  const allWorkerQualificationsString = useMemo(() => {
    if (workerTypesAvailableForHiring && workerTypesAvailableForHiring.length > 0) {
      return workerTypesAvailableForHiring.join(",");
    }

    return (
      workerQualifications?.map((qualification) => qualification.attributes.name).join(",") ?? ""
    );
  }, [workerQualifications, workerTypesAvailableForHiring]);

  // Filter tags to only include age group tags
  const ageGroupTags = useMemo(() => {
    if (!tagsData?.data) {
      return [];
    }

    return tagsData.data.filter((tag) => tag.attributes.metadata?.filterGroup === "ageGroup");
  }, [tagsData]);

  const defaultWorkerTypes = allWorkerQualificationsString;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FilterFormData>({
    resolver: zodResolver(filterFormSchema),
    defaultValues: {
      distanceInMiles: filter?.distanceInMiles?.$lt ?? 0,
      jobType: filter?.jobTypes ?? defaultJobTypes,
      shiftTime: filter?.shiftTypes ?? defaultShiftTypes,
      workerTypes: filter?.workerTypes ?? defaultWorkerTypes,
      daysOfWeek: filter?.daysOfWeek
        ? transformDaysOfWeekFromApiToForm(filter?.daysOfWeek)
        : defaultDaysOfWeek,
      ageGroups: filter?.tags ?? defaultAgeGroups,
    },
  });

  const watchedValues = useWatch({ control });

  // Add this useEffect to reset form values when filter changes
  // Don't reset while loading worker types
  useEffect(() => {
    if (isLoadingWorkerTypes) {
      return;
    }

    reset({
      distanceInMiles: filter?.distanceInMiles?.$lt ?? 0,
      jobType: filter?.jobTypes ?? defaultJobTypes,
      shiftTime: filter?.shiftTypes ?? defaultShiftTypes,
      workerTypes: filter?.workerTypes ?? defaultWorkerTypes,
      daysOfWeek: filter?.daysOfWeek
        ? transformDaysOfWeekFromApiToForm(filter?.daysOfWeek)
        : defaultDaysOfWeek,
      ageGroups: filter?.tags ?? defaultAgeGroups,
    });
  }, [
    filter,
    reset,
    defaultWorkerTypes,
    worker.licenses,
    isLoadingWorkerTypes,
    workerTypesAvailableForHiring,
  ]);

  function onSubmit(data: FilterFormData) {
    const workerTypeFilter = isEmpty(data.workerTypes)
      ? allWorkerQualificationsString // If workerTypes is empty, treat as all selected
      : data.workerTypes;
    const ageGroupsFilter = isEmpty(data.ageGroups) ? undefined : data.ageGroups;

    // Remove existing tags field to prevent carryover
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { tags: _unusedTags, ...filterWithoutTags } = filter ?? {};

    const filtersToSubmit = translateFilterForBackend({
      ...filterWithoutTags,
      distanceInMiles: data.distanceInMiles
        ? {
            $lt: data.distanceInMiles,
          }
        : undefined,
      jobTypes: data.jobType,
      shiftTypes: data.shiftTime,
      workerTypes: workerTypeFilter,
      daysOfWeek: data.daysOfWeek.replace(/\|/g, ","), // Convert back to comma delimiter for the API
      ageGroups: ageGroupsFilter,
    });
    setFilter(filtersToSubmit);
    logEvent(APP_V2_APP_EVENTS.PLACEMENTS_PAGE_ACTIONS, {
      placementCandidateId,
      action: "filter_applied",
      filters: { ...filtersToSubmit, distanceInMiles: data.distanceInMiles },
    });
    filterModalState.closeModal();
  }

  const totalAppliedFilters = countTotalAppliedFilters(watchedValues, defaultWorkerTypes);

  const open = useCallback(
    (eventOrSectionKey?: string | unknown) => {
      // Determine if the first argument is a section key (string) or a click event
      const maybeSectionKey =
        typeof eventOrSectionKey === "string" ? (eventOrSectionKey as SectionKey) : undefined;

      if (maybeSectionKey) {
        scrollTargetKeyRef.current = maybeSectionKey;
      }

      filterModalState.openModal();

      logEvent(APP_V2_APP_EVENTS.PLACEMENTS_PAGE_ACTIONS, {
        placementCandidateId,
        action: "filter_opened",
        filters: { ...filter, distanceInMiles: filter?.distanceInMiles?.$lt },
      });
    },
    [filterModalState, placementCandidateId, filter]
  );

  // After the modal is opened, scroll the corresponding section into view (only once)
  useEffect(() => {
    if (filterModalState.modalIsOpen && scrollTargetKeyRef.current) {
      const targetKey = scrollTargetKeyRef.current;
      const targetRef = sectionRefMap[targetKey];

      // Use setTimeout to allow the BottomSheet animation/layout to finish before scrolling
      // iOS needs more time than Android/Web
      setTimeout(() => {
        if (targetRef?.current) {
          targetRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 80);

      // Reset after scrolling so subsequent user scrolls are not disturbed
      scrollTargetKeyRef.current = undefined;
    }
  }, [filterModalState.modalIsOpen, sectionRefMap]);

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ open, totalAppliedFilters })
      ) : (
        <Button
          variant="outlined"
          color="primary"
          sx={{
            backgroundColor: (theme) => theme.palette.background.tertiary,
            "&:hover": {
              background: (theme) => theme.palette.background.tertiary,
            },
          }}
          startIcon={
            totalAppliedFilters > 0 ? (
              <Box
                sx={{
                  backgroundColor: (theme) => theme.palette.primary.main,
                  borderRadius: "50%",
                  height: "1.2rem",
                  width: "1.2rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text variant="caption" color="white">
                  {totalAppliedFilters}
                </Text>
              </Box>
            ) : (
              <CbhIcon type="slider-settings" />
            )
          }
          onClick={open}
        >
          <Text>Filters</Text>
        </Button>
      )}

      <BottomSheet
        disableSwipeArea
        modalState={filterModalState}
        footer={
          <StickyFooter>
            <DialogFooter
              orientation="horizontal"
              sx={{
                // sticky footer adds its own padding, so we need to remove it from the dialog footer
                py: 0,
              }}
              onClose={() => {
                filterModalState.closeModal();
              }}
            >
              <Stack
                direction="row"
                spacing={7}
                justifyContent={totalAppliedFilters > 0 ? "space-between" : "flex-end"}
                alignItems="center"
                flex={1}
              >
                {totalAppliedFilters > 0 && (
                  <TextButton
                    LinkComponent={Link}
                    size="large"
                    disabled={isLoadingWorkerTypes}
                    onClick={() => {
                      reset({
                        distanceInMiles: 0,
                        jobType: defaultJobTypes,
                        shiftTime: defaultShiftTypes,
                        workerTypes: allWorkerQualificationsString,
                        daysOfWeek: defaultDaysOfWeek,
                        ageGroups: defaultAgeGroups,
                      });
                      logEvent(APP_V2_APP_EVENTS.PLACEMENTS_PAGE_ACTIONS, {
                        placementCandidateId,
                        action: "filters_cleared",
                        filters: { ...filter, distanceInMiles: filter?.distanceInMiles?.$lt },
                      });
                    }}
                  >
                    Clear all
                  </TextButton>
                )}
                <Button
                  LinkComponent={Link}
                  size="large"
                  variant="contained"
                  disabled={isLoadingWorkerTypes}
                  onClick={handleSubmit(onSubmit)}
                >
                  View jobs
                </Button>
              </Stack>
            </DialogFooter>
          </StickyFooter>
        }
      >
        <Stack
          sx={{
            p: 7,
            opacity: isLoadingWorkerTypes ? 0.5 : 1,
            pointerEvents: isLoadingWorkerTypes ? "none" : "auto",
          }}
          spacing={8}
        >
          <Text semibold variant="h4" textAlign="center">
            Filters
          </Text>

          <Box ref={jobTypesRef}>
            <FilterChips
              name="jobType"
              control={control}
              options={jobTypeOptions}
              title="Job types"
              error={errors.jobType}
            />
          </Box>

          <Divider />

          <Box ref={shiftTypesRef}>
            <FilterChips
              name="shiftTime"
              control={control}
              options={shiftTypeOptions}
              title="Shift times"
              error={errors.shiftTime}
            />
          </Box>
          <Divider />

          <Box ref={daysOfWeekRef}>
            <FilterChips
              multiSelect
              allOptions
              name="daysOfWeek"
              control={control}
              delimiter="|"
              options={[
                {
                  label: "Weekdays",
                  value: "MONDAY,TUESDAY,WEDNESDAY,THURSDAY,FRIDAY",
                },
                {
                  label: "Weekends",
                  value: "SATURDAY,SUNDAY",
                },
              ]}
              title="Days of the week"
              error={errors.daysOfWeek}
            />
          </Box>
          <Divider />
          <Box ref={distanceInMilesRef}>
            <Controller
              name="distanceInMiles"
              control={control}
              render={({ field }) => {
                const value = field.value ?? MIN_DISTANCE;
                const handleChange = (newValue: number) => {
                  field.onChange(Math.max(newValue, MIN_DISTANCE));
                };

                return (
                  <Stack spacing={5}>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid
                        item
                        xs={8}
                        sx={{
                          "&.MuiGrid-item": {
                            pl: 0,
                          },
                        }}
                      >
                        <Text semibold variant="body1">
                          Distance from home
                        </Text>
                      </Grid>
                      <Grid item xs={4}>
                        <Input
                          size="small"
                          unit="miles"
                          value={value}
                          type="number"
                          inputProps={{
                            min: MIN_DISTANCE,
                          }}
                          error={value < MIN_DISTANCE}
                          onChange={(event) => {
                            const inputValue = Number(event.target.value);
                            handleChange(inputValue);
                          }}
                          onBlur={() => {
                            if (value < MIN_DISTANCE) {
                              handleChange(MIN_DISTANCE);
                            }
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Stack>
                      <DistanceSlider
                        aria-label="Placement filters distance slider"
                        value={value}
                        options={[10, 50, 100, 150]}
                        step={10}
                        max={150}
                        min={0}
                        onChange={(_event, sliderValue) => {
                          handleChange(sliderValue as number);
                        }}
                      />
                    </Stack>
                  </Stack>
                );
              }}
            />
          </Box>

          {workerTypeOptions.length > 1 && (
            <>
              <Divider />
              <Box ref={workerTypesRef}>
                <FilterChips
                  name="workerTypes"
                  control={control}
                  options={workerTypeOptions}
                  title="Licenses"
                  error={errors.workerTypes}
                />
              </Box>
            </>
          )}

          {!isEmpty(ageGroupTags) && (
            <>
              <Divider />
              <Box ref={ageGroupsRef}>
                <FilterChips
                  allOptions
                  showAnyOption
                  name="ageGroups"
                  control={control}
                  options={createTagOptions(ageGroupTags)}
                  title={getFilterGroupDisplayName("ageGroups")}
                  error={errors.ageGroups}
                />
              </Box>
            </>
          )}
        </Stack>
      </BottomSheet>
    </>
  );
}
